/**
 * vatVerifierSpec — `vat_verifier` capability (Acube VerifyID under the
 * hood; vendor name MUST NOT appear in FE per memory rule).
 *
 * No OAuth — API key registration only. The wizard collects the key (or
 * lets the user generate one via the BE) and writes it to vault.
 */

import type { BaseConnection, CapabilityWizardSpec } from "@ando/ui";
import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { BadgeCheck, FileSearch, Globe2, Shield } from "lucide-react";

import { createCapabilityApi } from "../api/capabilityApi";

export interface VatVerifierConnection extends BaseConnection {
  readonly key_label?: string;
}

const iconCls = "h-4 w-4";

export const vatVerifierSpec: CapabilityWizardSpec<VatVerifierConnection> = {
  capability: "vat_verifier",
  title: msg`Verifica P.IVA`,
  subtitle: msg`Validazione anagrafiche, VIES, controllo soggetti`,
  icon: <BadgeCheck className="h-5 w-5" aria-hidden />,
  gdprDeleteWindowMinutes: 15,

  vendors: [
    {
      id: "vat_verifier",
      label: "VAT Verifier",
      icon: <BadgeCheck className={iconCls} aria-hidden />,
      whiteLabel: true,
    },
  ],

  steps: [
    {
      id: "creds",
      kind: "credentials",
      title: msg`Chiave API`,
      description: msg`Incolla la chiave API ottenuta dal provider (o lascia vuoto per usare il piano gestito).`,
      fields: [
        { name: "key_label", label: msg`Etichetta`, type: "string", placeholder: "Studio Rossi" },
        { name: "api_key", label: "API Key", type: "password" },
      ],
    },
    { id: "confirm", kind: "confirm", title: msg`Conferma` },
  ],

  benefits: [
    {
      icon: <FileSearch className={iconCls} aria-hidden />,
      title: msg`VIES integrato`,
      body: msg`Verifica P.IVA UE in tempo reale dentro al flusso fatturazione.`,
    },
    {
      icon: <BadgeCheck className={iconCls} aria-hidden />,
      title: msg`Anagrafica completa`,
      body: msg`Ragione sociale, codice ATECO, sede legale — tutto in chat.`,
    },
    {
      icon: <Globe2 className={iconCls} aria-hidden />,
      title: msg`Multi-paese`,
      body: msg`Italia, EU, UK in un'unica API.`,
    },
    {
      icon: <Shield className={iconCls} aria-hidden />,
      title: msg`Rate-limit gestito`,
      body: msg`Cache + retry trasparenti, niente blocchi sul portale AdE.`,
    },
  ],

  api: createCapabilityApi<VatVerifierConnection>({
    capability: "vat_verifier",
    defaultVendor: "vat_verifier",
  }),
  connectionKeyField: "id" as keyof VatVerifierConnection,
  getDisplayName: (c) => c.key_label ?? c.id ?? i18nSingleton._(msg`Chiave`),
};
